/* eslint-disable react/jsx-no-bind */
/* eslint-disable react/forbid-prop-types */
import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { globalAccountsView, postData } from '../../redux/reducers/data';
import { setMessage } from '../../redux/actions/message';
import AccountLabel from '../../elements/AccountLabel';
import SublistOkCancel from './SublistOkCancel';

// handles loanInterest, cost, contribution
export default function BlueTransactionsCosts({ selectedOption, accountId, handleCloseAfterSave, handleCancel, linkedAttribute, transactions }) {
  const { t } = useTranslation('accountDetails', { keyPrefix: 'deposits' });
  const [loading, setLoading] = useState(false);
  const [localLinkedAttribute, setLocalLinkedAttribute] = useState(linkedAttribute); // intialise to the object from props (should there be an existing linkedAttribute already)
  const dispatch = useDispatch();
  const allAccounts = useSelector(globalAccountsView) || [];
  const accountIdsConnectedToThis = allAccounts.filter((account) => account.connectedDepositAccounts?.includes(accountId))?.map((account) => account.id);

  const accountsFilteredByConnectedAccounts = allAccounts
    .filter((account) => (accountIdsConnectedToThis ? accountIdsConnectedToThis?.includes(account.id) : true))
    .filter((account) => (selectedOption.id === 'investment-contribution' ? account.category === 'pension' : true)) // only show pension accounts for contributions
    .filter((account) => (selectedOption.id === 'investment-loanInterest' ? account.category === 'loans' : true)); // only show loan accounts for interest

  async function handleSave() {
    if (transactions.length === 0) return;

    // check if a transaction is selected
    if (!localLinkedAttribute?.accountId) {
      dispatch(setMessage('symbolNotSelected'));
      return;
    }

    const updatedTransactions = [];

    try {
      transactions.forEach((transaction) => {
        const updatedTransaction = {
          ...transaction,
          label: selectedOption.id,
          // courtesy adjustment of costs (must be a negative number)
          amount: -Math.abs(transaction.amount),
          importFlag: 'put',
        };

        // save the assetId and accountId to the transaction tags
        updatedTransaction.tags = {
          ...(typeof updatedTransaction.tags === 'string' ? JSON.parse(updatedTransaction.tags) : updatedTransaction.tags),
          assetId: localLinkedAttribute.assetId,
          accountId: localLinkedAttribute.accountId,
        };
        updatedTransactions.push(updatedTransaction);
      });
    } catch (e) {
      return; // we already show an error message
    }

    setLoading(true);
    try {
      await dispatch(
        postData({
          data: updatedTransactions,
          category: 'deposits',
          accountId,
        }),
      );
    } catch (err) {
      setLoading(false);
      dispatch(setMessage('dataUpdateError'));
    }

    setLoading(false);
    handleCloseAfterSave();
  }

  if (transactions.length === 0) return null;

  return (
    <>
      <div className="flex flex-col gap-2 overflow-y-auto" data-testid="blue-transactions-costs">
        {accountsFilteredByConnectedAccounts.map((x) => (
          // ^^ returns an array of account objects
          <button
            type="button"
            key={x.id}
            onClick={() => setLocalLinkedAttribute({
              ...localLinkedAttribute,
              accountId: x.id,
              assetId: x.id,
            })}
            // ^^ assetId added to enable mapping of account-level costs onto account also for stocks;
            // commonAssetView will map the account level costs to an accountId > assetId = accountId
            data-testid="sublist-account"
            className={`text-xs border border-gray-200 shadow-sm rounded-md p-2 flex justify-between gap-2 cursor-pointer
          ${x?.id === localLinkedAttribute?.accountId ? 'bg-brandBlue-500 text-white hover:bg-brandBlue-600 border-transparent' : 'bg-white text-gray-900 hover:bg-gray-50'}`}
          >
            <AccountLabel accountName={x?.name} category={x?.category} />
          </button>
        ))}
        {accountsFilteredByConnectedAccounts.length === 0 && <div className="text-sm text-gray-500 self-center mt-2">{t('noAccounts')}</div>}
      </div>
      <div className="space-y-2">
        <SublistOkCancel handleSave={handleSave} handleCancel={handleCancel} loading={loading} />
      </div>
    </>
  );
}
BlueTransactionsCosts.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  selectedOption: PropTypes.objectOf(PropTypes.any).isRequired,
  accountId: PropTypes.string.isRequired,
  linkedAttribute: PropTypes.object,
  handleCloseAfterSave: PropTypes.func.isRequired,
  handleCancel: PropTypes.func.isRequired,
  transactions: PropTypes.arrayOf(PropTypes.object).isRequired,
};
BlueTransactionsCosts.defaultProps = {
  linkedAttribute: {},
};
